import { useEffect, useState } from "react";
import { Mail, MailWarning, MailCheck, Send, Clock, AlertTriangle, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

import { api, formatDate } from "@/lib/api";

const Stat = ({ icon: Icon, label, value, tone, testid }) => (
  <div
    data-testid={testid}
    className="bg-white border border-gray-200 rounded-xl p-5 shadow-[0_2px_12px_rgba(0,0,0,0.04)]"
  >
    <div className="flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-gray-500">
      <Icon size={12} />
      {label}
    </div>
    <div
      className={`mt-2 font-display font-bold text-3xl tracking-tight ${
        tone === "danger" ? "text-red-600" : tone === "primary" ? "text-[#0055FF]" : "text-gray-900"
      }`}
    >
      {value ?? "—"}
    </div>
  </div>
);

export default function EmailNotificationsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email_log");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load email log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleResend = async (row) => {
    setResending(row.id);
    try {
      await api.post(`/receipts/${row.id}/resend_email`);
      toast.success(`Email notification sent to ${row.customer_email}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setResending(null);
    }
  };

  if (loading || !data) {
    return (
      <div className="mt-8 px-5 py-12 text-center text-gray-500" data-testid="email-loading">
        Loading email notifications…
      </div>
    );
  }

  const s = data.summary || {};
  const rows = data.rows || [];

  return (
    <div data-testid="email-panel" className="mt-8">
      {/* Summary */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={MailCheck} label="Total sent" value={s.sent} tone="primary" testid="email-stat-sent" />
        <Stat icon={MailWarning} label="Total failed" value={s.failed} tone="danger" testid="email-stat-failed" />
        <Stat icon={Send} label="Sent · 24h" value={s.sent_24h} testid="email-stat-sent-24h" />
        <Stat icon={AlertTriangle} label="Failed · 24h" value={s.failed_24h} tone={s.failed_24h ? "danger" : undefined} testid="email-stat-failed-24h" />
        <Stat
          icon={Clock}
          label="Last email"
          value={s.last_email_at ? formatDate(s.last_email_at) : "—"}
          testid="email-stat-last"
        />
      </div>

      {/* Table */}
      <div className="mt-6 flex items-end justify-between mb-3">
        <h2 className="font-display font-semibold text-xl text-gray-900">Recent attempts</h2>
        <button
          onClick={load}
          data-testid="email-refresh"
          className="inline-flex items-center gap-1.5 bg-white text-gray-700 border border-gray-200 text-sm font-medium px-3 py-2 rounded-md hover:border-[#0055FF] hover:text-[#0055FF]"
        >
          <RefreshCcw size={14} />
          Refresh
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
        {rows.length === 0 ? (
          <div className="px-5 py-12 text-center text-gray-500 text-sm">
            No email attempts yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="email-log-table">
              <thead>
                <tr className="bg-gray-50/70 text-left text-[11px] font-semibold tracking-wider uppercase text-gray-500 border-b border-gray-200">
                  <th className="px-5 py-3">Receipt</th>
                  <th className="px-5 py-3">Customer</th>
                  <th className="px-5 py-3">Certificate</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Sent At</th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const failed =
                    r.certificate_email_sent === false &&
                    r.certificate_email_error;
                  const sent = !!r.certificate_email_sent;
                  return (
                    <tr
                      key={r.id}
                      data-testid={`email-row-${r.id}`}
                      className="border-b border-gray-100 last:border-0 hover:bg-gray-50/60"
                    >
                      <td className="px-5 py-3">
                        <div className="font-mono text-xs text-gray-900">{r.receipt_id}</div>
                        {r.source_order_id && (
                          <div className="font-mono text-[11px] text-gray-400">Order {r.source_order_id}</div>
                        )}
                      </td>
                      <td className="px-5 py-3 text-gray-700">{r.customer_email}</td>
                      <td className="px-5 py-3 font-mono text-xs text-gray-700">
                        {r.token_id ? `Token ${r.token_id}` : r.nft_id || "—"}
                      </td>
                      <td className="px-5 py-3">
                        {sent && (
                          <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-full text-xs font-semibold">
                            <Mail size={11} /> Sent
                          </span>
                        )}
                        {failed && (
                          <div>
                            <span className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-full text-xs font-semibold">
                              <MailWarning size={11} /> Failed
                            </span>
                            <div className="mt-1 text-[11px] text-red-700 max-w-[260px] truncate" title={r.certificate_email_error}>
                              {r.certificate_email_error}
                            </div>
                          </div>
                        )}
                        {!sent && !failed && (
                          <span className="text-xs text-gray-500">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-gray-600 text-xs font-mono">
                        {r.certificate_email_sent_at
                          ? new Date(r.certificate_email_sent_at).toLocaleString()
                          : "—"}
                        {r.resent_at && (
                          <div className="text-[10px] text-gray-400">resent {new Date(r.resent_at).toLocaleTimeString()}</div>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        {failed && (
                          <button
                            onClick={() => handleResend(r)}
                            disabled={resending === r.id}
                            data-testid={`email-resend-${r.id}`}
                            className="inline-flex items-center gap-1.5 bg-[#0055FF] text-white text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-[#0044CC] disabled:opacity-60"
                          >
                            <Send size={12} />
                            {resending === r.id ? "Sending…" : "Resend"}
                          </button>
                        )}
                        {sent && (
                          <button
                            onClick={() => handleResend(r)}
                            disabled={resending === r.id}
                            data-testid={`email-resend-${r.id}`}
                            className="inline-flex items-center gap-1.5 bg-white text-gray-700 border border-gray-200 text-xs font-semibold px-3 py-1.5 rounded-md hover:border-[#0055FF] hover:text-[#0055FF] disabled:opacity-60"
                            title="Send again"
                          >
                            <RefreshCcw size={12} />
                            {resending === r.id ? "…" : "Resend"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
